import React, { useState, useEffect, useCallback, useMemo } from "react";
import { IContextState, IContextProps } from "./ClusterCachedDataContextInterfaces";
import { ClusterCachedDataContext } from "./ClusterCachedDataContext";
import {
    getClusterDetailsClusterStore,
    getClusterIdHMI,
    getMQTTBrokerSettingsHMIStore,
    isTokenAvailable,
    setLocalStorageItem,
} from "../../utilities/storageUtilities";
import { UPDATEMINUTESRATEFORHMIDATA } from "constants/constants";
import { initialStateClusterCachedDataContext } from "./ClusterCachedDataContext";
import { getInfoFromCloudserver, getInfoFromLocalserver, setCachedData, compressHMIData } from "./helpers";
import { LocalStorageCachedDataKeysCluster } from "constants/constants";

export const ClusterCachedDataContextProvider: React.FC<IContextProps> = ({ children }) => {
    const [store, setStore] = useState<IContextState["store"]>(initialStateClusterCachedDataContext.store);

    const token = isTokenAvailable();
    const cluster = getClusterIdHMI();

    const GetClusterData = useCallback(async (cluster_id: number, login?: boolean) => {
        try {
            const clusterDetailsLocalStorage = getClusterDetailsClusterStore();
            const mqttBrokerSettingsLocalStorage = getMQTTBrokerSettingsHMIStore();

            if (login) {
                await getInfoFromCloudserver(cluster_id, setStore);
            } else {
                if (mqttBrokerSettingsLocalStorage?.[0] && clusterDetailsLocalStorage?.dedicated_localserver) {
                    try {
                        await getInfoFromLocalserver(cluster_id, setStore, mqttBrokerSettingsLocalStorage?.[0]);
                    } catch {
                        await getInfoFromCloudserver(cluster_id, setStore);
                    }
                } else {
                    await getInfoFromCloudserver(cluster_id, setStore);
                }
            }
        } catch (error: any) {
            setCachedData(setStore, error);
            throw error;
        }
    }, []);

    const resetClusterStore = useCallback(
        () =>
            setStore({
                ...initialStateClusterCachedDataContext.store,
            }),
        [],
    );

    const reorderMachineCentersCluster = useCallback((orderedMachineCenterIds: number[]) => {
        setStore((prev) => {
            const idToMachineCenter = new Map(prev.machineCentersCluster.map((mc) => [mc.machine_center_id, mc]));
            const ordered: typeof prev.machineCentersCluster = [];

            for (const id of orderedMachineCenterIds) {
                const mc = idToMachineCenter.get(id);
                if (mc) ordered.push(mc);
            }

            if (ordered.length !== prev.machineCentersCluster.length) {
                for (const mc of prev.machineCentersCluster) {
                    if (!ordered.some((o) => o.machine_center_id === mc.machine_center_id)) ordered.push(mc);
                }
            }

            const compressedHMIData = compressHMIData(ordered);
            setLocalStorageItem(LocalStorageCachedDataKeysCluster.MACHINE_CENTERS_CLUSTER, JSON.stringify(ordered));

            return {
                ...prev,
                machineCentersCluster: ordered,
                compressedHMIData,
            };
        });
    }, []);

    const getData = useCallback(async () => {
        if (token && cluster) await GetClusterData(cluster);
    }, [GetClusterData, cluster, token]);

    useEffect(() => {
        if (cluster) {
            getData();
            const interval = setInterval(async () => {
                await getData();
            }, UPDATEMINUTESRATEFORHMIDATA);

            return () => clearInterval(interval);
        }
    }, [getData, cluster]);

    const providerValues = useMemo(
        () => ({
            store,
            dispatch: {
                GetClusterData,
                resetClusterStore,
                reorderMachineCentersCluster,
            },
        }),
        [store, GetClusterData, resetClusterStore, reorderMachineCentersCluster],
    );

    return <ClusterCachedDataContext.Provider value={providerValues}>{children}</ClusterCachedDataContext.Provider>;
};
